export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface ItemPriceArgs {
  unitPrice: number;
  optionsTotal: number;
  quantity: number;
}

export function computeItemTotal({ unitPrice, optionsTotal, quantity }: ItemPriceArgs): number {
  const qty = Math.max(0, Math.floor(quantity));
  return (unitPrice + optionsTotal) * qty;
}

export type DiscountType = "fixed" | "percent" | null | undefined;

export function applyDiscount(
  subtotal: number,
  discountValue: number | string | null | undefined,
  discountType: DiscountType,
): number {
  if (discountValue === null || discountValue === undefined || discountValue === "") return 0;
  if (!discountType) return 0;
  const v = Number(discountValue);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (discountType === "percent") {
    const pct = Math.min(100, Math.max(0, v));
    return round2((subtotal * pct) / 100);
  }
  return Math.min(subtotal, round2(v));
}

export interface CardSurchargeConfig {
  enabled?: boolean;
  type?: "percent" | "fixed";
  value?: number | string;
  includeDelivery?: boolean;
}

export type CardSurchargeMap = Record<string, CardSurchargeConfig | undefined> | null | undefined;

export function computeCardSurcharge(
  cfgMap: CardSurchargeMap,
  paymentMethod: string,
  baseTotal: number,
  deliveryFee: number,
): number {
  const cfg = cfgMap?.[paymentMethod];
  if (!cfg || !cfg.enabled) return 0;
  const v = Number(cfg.value);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (cfg.type === "percent") {
    const reference = cfg.includeDelivery ? baseTotal + deliveryFee : baseTotal;
    return round2(reference * (v / 100));
  }
  return round2(v);
}

export interface OrderTotalArgs {
  subtotal: number;
  discount: number;
  deliveryFee: number;
  surcharge: number;
}

export function computeOrderTotal({ subtotal, discount, deliveryFee, surcharge }: OrderTotalArgs): number {
  return Math.max(0, round2(subtotal - discount + deliveryFee + surcharge));
}

export function computeCashChange(received: number, total: number): number {
  if (received <= total) return 0;
  return round2(received - total);
}

export interface ApplyClientUnitPriceArgs {
  originalPrice: number;
  promoActive: boolean;
  promoFinalPrice: number;
  clientUnitPrice: number | null | undefined;
}

/**
 * Server-side validation of a client-supplied unit price snapshot.
 * Allows the unit price to fall anywhere within [promoFinalPrice, originalPrice]
 * (with a 0.005 tolerance) to support promotions while preventing tampering.
 * Returns a rounded unit price.
 */
export function applyClientUnitPrice({
  originalPrice,
  promoActive,
  promoFinalPrice,
  clientUnitPrice,
}: ApplyClientUnitPriceArgs): number {
  const fallback = promoActive ? promoFinalPrice : originalPrice;
  if (clientUnitPrice === null || clientUnitPrice === undefined) return fallback;
  const cu = Number(clientUnitPrice);
  if (!Number.isFinite(cu)) return fallback;
  const minAllowed = promoActive ? promoFinalPrice : originalPrice;
  if (cu >= minAllowed - 0.005 && cu <= originalPrice + 0.005) {
    return Math.max(0, round2(cu));
  }
  return fallback;
}

/** Configuração da promoção por quantidade (espelho de ProductPromotion.quantity
 * em @workspace/db — duplicado aqui porque esta lib não depende do schema). */
export interface QuantityPromoConfig {
  mode: "threshold" | "every_nth" | "progressive" | "all_from";
  fromUnit?: number;
  n?: number;
  discountType?: "percent" | "fixed";
  discountValue?: number;
  step?: { discountType: "percent" | "fixed"; discountValue: number };
  maxDiscount?: number;
}

function promoUnitPrice(basePrice: number, type: "percent" | "fixed", value: number): number {
  if (type === "percent") {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return round2(basePrice * (1 - pct / 100));
  }
  // fixed = PREÇO promocional da unidade (nunca acima do original, nunca < 0)
  return round2(Math.min(basePrice, Math.max(0, Number(value) || 0)));
}


/** O selo da promoção por quantidade, com as frases escolhidas pelo Pedro
 * (09/08): sem "off" — "de desconto" é a língua do balcão. Leve N pague N−1 é
 * o caso especial de 100%. É o texto do link (venda-no-zap lib/promotion.ts,
 * que ainda tem a cópia dele) e o que o robô que conversa fala. */
export function quantityPromoLabel(cfg: QuantityPromoConfig | null | undefined): string | null {
  if (!cfg) return null;
  const money = (v?: number) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
  if (cfg.mode === "threshold" && cfg.fromUnit) {
    return cfg.discountType === "fixed"
      ? `${money(cfg.discountValue)} cada, da ${cfg.fromUnit}ª em diante`
      : `${cfg.discountValue}% de desconto da ${cfg.fromUnit}ª em diante`;
  }
  if (cfg.mode === "all_from" && cfg.fromUnit) {
    return cfg.discountType === "fixed"
      ? `${money(cfg.discountValue)} cada, levando ${cfg.fromUnit} ou mais`
      : `${cfg.discountValue}% de desconto levando ${cfg.fromUnit} ou mais`;
  }
  if (cfg.mode === "every_nth" && cfg.n) {
    if (cfg.discountType === "percent" && Number(cfg.discountValue) >= 100) {
      return `Leve ${cfg.n}, pague ${cfg.n - 1}`;
    }
    return cfg.discountType === "fixed"
      ? `A cada ${cfg.n}, 1 sai por ${money(cfg.discountValue)}`
      : `A cada ${cfg.n}, 1 sai com ${cfg.discountValue}% de desconto`;
  }
  if (cfg.mode === "progressive" && cfg.step) {
    const cap = cfg.step.discountType === "percent"
      ? `${cfg.maxDiscount}%`
      : money(cfg.maxDiscount);
    return `Leve mais, pague menos: até ${cap} de desconto`;
  }
  return null;
}

/**
 * Desconto POR UNIDADE numa promoção por quantidade. ÚNICA fonte da conta:
 * client (use-cart/use-pdv-cart) e server (catalog/orders, pdv, orders) usam
 * esta função — fórmula em dois lugares diverge e o pedido passa a falhar
 * (lição do pagamento misto).
 *
 * Entrada: o VALOR de cada unidade (preço-base + adicionais da linha dela) —
 * loja de roupa modela tamanho como adicional com o preço no adicional e
 * base R$ 0, então descontar só o base zerava a promoção (caso Blusa,
 * 09/08/2026). Saída: desconto por unidade, alinhado à ordem de entrada.
 *
 * Atribuição merchant-safe: as unidades são ranqueadas por valor DECRESCENTE
 * e a posição k do rank recebe o desconto da regra — as mais caras ficam
 * cheias e o desconto cai nas mais baratas ("leve 3 pague 2" dá de graça a
 * mais barata, como no varejo).
 *
 * - threshold: a Nª unidade JÁ sai com desconto (decisão do Pedro, 09/08).
 * - all_from: levando N ou mais (N = fromUnit), TODAS saem com desconto, inclusive a 1ª; com
 *   menos de N, nenhuma (Pedro, 10/10/2026, Chocojo: mousse a R$ 17, 2 ou mais a R$ 13 cada).
 * - every_nth: a cada N, a N-ésima sai com desconto (leve 3 pague 2 = n:3, 100%).
 * - progressive: unidade k tem desconto min(step×(k−1), teto); teto
 *   OBRIGATÓRIO (sem teto → sem promoção; o cadastro impede salvar assim).
 * - "fixed" em threshold/every_nth = PREÇO promocional da unidade
 *   (desconto = max(0, valor − preço)); no progressive o step fixed é R$ de
 *   desconto por unidade extra.
 * - Config inválida/incompleta → desconto zero (fail-open, nunca barra venda).
 */
export function assignQuantityPromoDiscounts(
  unitValues: number[],
  cfg: QuantityPromoConfig | null | undefined,
): number[] {
  const values = unitValues.map((v) => round2(Math.max(0, Number(v) || 0)));
  const zeros = values.map(() => 0);
  if (values.length === 0 || !cfg) return zeros;

  const discountForRank = (k: number, value: number): number => {
    if (cfg.mode === "all_from") {
      const from = Math.max(2, Math.trunc(Number(cfg.fromUnit) || 0));
      if (!cfg.discountType || !Number.isFinite(Number(cfg.discountValue)) || values.length < from) return 0;
      return round2(value - promoUnitPrice(value, cfg.discountType, Number(cfg.discountValue)));
    }
    if (cfg.mode === "threshold") {
      const from = Math.max(2, Math.trunc(Number(cfg.fromUnit) || 0));
      if (!cfg.discountType || !Number.isFinite(Number(cfg.discountValue)) || k < from) return 0;
      return round2(value - promoUnitPrice(value, cfg.discountType, Number(cfg.discountValue)));
    }
    if (cfg.mode === "every_nth") {
      const n = Math.max(2, Math.trunc(Number(cfg.n) || 0));
      if (!cfg.discountType || !Number.isFinite(Number(cfg.discountValue)) || k % n !== 0) return 0;
      return round2(value - promoUnitPrice(value, cfg.discountType, Number(cfg.discountValue)));
    }
    if (cfg.mode === "progressive") {
      const step = cfg.step;
      const cap = Number(cfg.maxDiscount);
      if (!step || !Number.isFinite(Number(step.discountValue)) || !Number.isFinite(cap) || cap <= 0) {
        return 0; // sem teto não há promoção (o cadastro exige teto)
      }
      const stepVal = Math.max(0, Number(step.discountValue));
      if (step.discountType === "percent") {
        const pct = Math.min(Math.min(100, cap), stepVal * (k - 1));
        return round2(value * (pct / 100));
      }
      return round2(Math.min(Math.min(cap, stepVal * (k - 1)), value));
    }
    return 0;
  };

  // Rank estável por valor DESC: índice de entrada carrega o desempate.
  const order = values
    .map((value, idx) => ({ value, idx }))
    .sort((a, b) => (b.value - a.value) || (a.idx - b.idx));
  const out = zeros.slice();
  order.forEach(({ value, idx }, i) => {
    out[idx] = discountForRank(i + 1, value);
  });
  return out;
}

export interface FreeDeliveryArgs {
  deliveryFee: number;
  subtotalAfterDiscount: number;
  freeDeliveryAbove: number | string | null | undefined;
}

export function applyFreeDelivery({
  deliveryFee,
  subtotalAfterDiscount,
  freeDeliveryAbove,
}: FreeDeliveryArgs): { fee: number; freeApplied: boolean } {
  const threshold = Number(freeDeliveryAbove);
  if (Number.isFinite(threshold) && threshold > 0 && subtotalAfterDiscount >= threshold && deliveryFee > 0) {
    return { fee: 0, freeApplied: true };
  }
  return { fee: deliveryFee, freeApplied: false };
}
